/** Shared helpers: detect AI post-op sections and parse HTML into structured aftercare / red flags. */

export function isPostOpSectionTitle(title) {
  const t = String(title || '').toLowerCase();
  return (
    t.includes('post-operative') ||
    t.includes('post operative') ||
    t.includes('post-op') ||
    t.includes('postop') ||
    (t.includes('aftercare') && t.includes('instruction'))
  );
}

function stripHtml(text) {
  return String(text || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractListItems(html) {
  if (!html || typeof html !== 'string') return [];
  return [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((m) => stripHtml(m[1]))
    .filter(Boolean);
}

function extractIntro(html) {
  if (!html || typeof html !== 'string') return '';
  const match = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  return match ? stripHtml(match[1]) : '';
}

const URGENT_SPLIT = /call urgently|urgently if|seek urgent|contact (the )?clinic urgently|warning signs|red flags|if you (experience|notice)/i;

/**
 * Parse AI-generated post-op HTML into { intro, instructions, redFlags }.
 * @param {string} html
 * @returns {{ intro?: string, instructions: string[], redFlags: string[] } | null}
 */
export function parsePostOpSectionHtml(html) {
  if (!html || typeof html !== 'string') return null;

  const intro = extractIntro(html);
  const allItems = extractListItems(html);
  if (!allItems.length) return null;

  const splitAt = html.search(URGENT_SPLIT);
  if (splitAt > -1) {
    const before = html.slice(0, splitAt);
    const after = html.slice(splitAt);
    const instructions = extractListItems(before);
    const redFlags = extractListItems(after);
    if (instructions.length || redFlags.length) {
      return {
        intro: intro || undefined,
        instructions: instructions.length ? instructions : allItems.slice(0, Math.ceil(allItems.length / 2)),
        redFlags: redFlags.length ? redFlags : allItems.slice(Math.ceil(allItems.length / 2)),
      };
    }
  }

  const mid = Math.max(1, Math.ceil(allItems.length / 2));
  return {
    intro: intro || undefined,
    instructions: allItems.slice(0, mid),
    redFlags: allItems.slice(mid),
  };
}

/**
 * Pull post-op out of section list; return filtered sections for inline document display.
 * @param {Array<{title?: string, content?: string}>} sections
 */
export function extractPostOpFromSections(sections) {
  if (!Array.isArray(sections)) return { postOp: null, sections: sections || [] };

  let postOp = null;
  let postOpTitle = null;
  const filtered = sections.filter((section) => {
    if (!isPostOpSectionTitle(section?.title)) return true;
    postOpTitle = section.title;
    postOp = parsePostOpSectionHtml(section.content);
    return false;
  });

  if (postOp && postOpTitle) {
    postOp = { title: postOpTitle, ...postOp };
  }

  return { postOp, sections: filtered };
}

/** Find and parse post-op from consent document (content + templates). */
export function findParsedPostOpInConsent(consent) {
  if (!consent) return null;
  if (consent.postOp?.instructions?.length) {
    return {
      title: consent.postOp.title,
      intro: consent.postOp.intro,
      instructions: consent.postOp.instructions,
      redFlags: consent.postOp.redFlags || [],
    };
  }

  const pools = [
    ...(consent.content?.sections || []),
    ...(consent.templates || []).flatMap((t) => t?.sections || []),
  ];
  const section = pools.find((s) => isPostOpSectionTitle(s?.title));
  if (!section) return null;

  const parsed = parsePostOpSectionHtml(section.content);
  if (!parsed?.instructions?.length) return null;

  return {
    title: section.title,
    intro: parsed.intro,
    instructions: parsed.instructions,
    redFlags: parsed.redFlags || [],
  };
}
